"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { z } from 'zod'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type ShipmentFormValues = {
  shipperId: string
  consigneeId?: string
  cargoSegment: string
  weightTons: string
  originLocationId: string
  destinationLocationId: string
  pickupEarliest: string
  pickupLatest: string
  deliveryDeadline?: string
  marketType: string
  modeConstraint?: string
  currency?: string
  visibility?: string
  commodityDescription?: string
  notes?: string
}

const cargoSegmentOptions = [
  'container_iso', 'swap_body', 'semi_trailer', 'bulk_dry', 'bulk_liquid', 'bulk_gas',
  'palletized', 'general_cargo', 'parcels', 'hazmat', 'reefer', 'vehicle', 'oversized', 'timber', 'steel',
]

const marketTypeOptions = ['contract', 'spot', 'programmatic']
const modeConstraintOptions = ['auto', 'road_only', 'rail_only', 'intermodal_only', 'sea_required']
const visibilityOptions = ['private', 'tenant', 'invited']

export default function CreateShipmentPage() {
  const t = useT()
  const router = useRouter()

  const fields = React.useMemo(
    () => [
      { id: 'shipperId', label: t('marketplace.fields.shipperId', 'Shipper ID'), type: 'text' as const, required: true },
      { id: 'consigneeId', label: t('marketplace.fields.consigneeId', 'Consignee ID'), type: 'text' as const },
      { id: 'cargoSegment', label: t('marketplace.fields.cargoSegment', 'Cargo segment'), type: 'select' as const, required: true, options: cargoSegmentOptions.map((value) => ({ value, label: t(`marketplace.cargoSegment.${value}`, value) })) },
      { id: 'weightTons', label: t('marketplace.fields.weightTons', 'Weight (tons)'), type: 'number' as const, required: true },
      { id: 'originLocationId', label: t('marketplace.fields.originLocationId', 'Origin location ID'), type: 'text' as const, required: true },
      { id: 'destinationLocationId', label: t('marketplace.fields.destinationLocationId', 'Destination location ID'), type: 'text' as const, required: true },
      { id: 'pickupEarliest', label: t('marketplace.fields.pickupEarliest', 'Pickup earliest'), type: 'datetime-local' as const, required: true },
      { id: 'pickupLatest', label: t('marketplace.fields.pickupLatest', 'Pickup latest'), type: 'datetime-local' as const, required: true },
      { id: 'deliveryDeadline', label: t('marketplace.fields.deliveryDeadline', 'Delivery deadline'), type: 'datetime-local' as const },
      { id: 'marketType', label: t('marketplace.fields.marketType', 'Market type'), type: 'select' as const, required: true, options: marketTypeOptions.map((value) => ({ value, label: t(`marketplace.marketType.${value}`, value) })) },
      { id: 'modeConstraint', label: t('marketplace.fields.modeConstraint', 'Mode constraint'), type: 'select' as const, options: modeConstraintOptions.map((value) => ({ value, label: t(`marketplace.modeConstraint.${value}`, value) })) },
      { id: 'currency', label: t('marketplace.fields.currency', 'Currency'), type: 'text' as const },
      { id: 'visibility', label: t('marketplace.fields.visibility', 'Visibility'), type: 'select' as const, options: visibilityOptions.map((value) => ({ value, label: t(`marketplace.visibility.${value}`, value) })) },
      { id: 'commodityDescription', label: t('marketplace.fields.commodityDescription', 'Commodity description'), type: 'textarea' as const },
      { id: 'notes', label: t('marketplace.fields.notes', 'Notes'), type: 'textarea' as const },
    ],
    [t],
  )

  const groups = React.useMemo(
    () => [
      { id: 'main', title: t('marketplace.form.groups.main', 'Shipment details'), fields: ['shipperId', 'consigneeId', 'cargoSegment', 'weightTons'] },
      { id: 'route', title: t('marketplace.form.groups.route', 'Route'), fields: ['originLocationId', 'destinationLocationId'] },
      { id: 'timing', title: t('marketplace.form.groups.timing', 'Timing'), fields: ['pickupEarliest', 'pickupLatest', 'deliveryDeadline'] },
      { id: 'market', title: t('marketplace.form.groups.market', 'Market'), fields: ['marketType', 'modeConstraint', 'currency', 'visibility'] },
      { id: 'other', title: t('marketplace.form.groups.other', 'Additional'), fields: ['commodityDescription', 'notes'] },
    ],
    [t],
  )

  const schema = React.useMemo(
    () =>
      z.object({
        shipperId: z.string().min(1),
        consigneeId: z.string().optional(),
        cargoSegment: z.string().min(1),
        weightTons: z.string().min(1),
        originLocationId: z.string().min(1),
        destinationLocationId: z.string().min(1),
        pickupEarliest: z.string().min(1),
        pickupLatest: z.string().min(1),
        deliveryDeadline: z.string().optional(),
        marketType: z.string().min(1),
        modeConstraint: z.string().optional(),
        currency: z.string().optional(),
        visibility: z.string().optional(),
        commodityDescription: z.string().optional(),
        notes: z.string().optional(),
      }),
    [],
  )

  const initialValues: Partial<ShipmentFormValues> = {
    modeConstraint: 'auto',
    currency: 'PLN',
    visibility: 'private',
    marketType: 'spot',
  }

  return (
    <Page>
      <PageBody>
        <CrudForm<ShipmentFormValues>
          title={t('marketplace.pages.create.title', 'Create shipment')}
          backHref="/backend/marketplace"
          cancelHref="/backend/marketplace"
          submitLabel={t('marketplace.pages.create.submit', 'Create shipment')}
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          schema={schema}
          onSubmit={async (values) => {
            const payload = {
              shipperId: values.shipperId,
              consigneeId: values.consigneeId || undefined,
              cargoSegment: values.cargoSegment,
              weightTons: Number(values.weightTons),
              originLocationId: values.originLocationId,
              destinationLocationId: values.destinationLocationId,
              pickupEarliest: new Date(values.pickupEarliest).toISOString(),
              pickupLatest: new Date(values.pickupLatest).toISOString(),
              deliveryDeadline: values.deliveryDeadline ? new Date(values.deliveryDeadline).toISOString() : undefined,
              marketType: values.marketType,
              modeConstraint: values.modeConstraint || 'auto',
              currency: values.currency || 'PLN',
              visibility: values.visibility || 'private',
              commodityDescription: values.commodityDescription || undefined,
              notes: values.notes || undefined,
            }
            const created = await createCrud<{ id?: string; shipmentNumber?: string }>(
              'freight/marketplace/shipments',
              payload,
              { errorMessage: t('marketplace.pages.create.saveError', 'Failed to create shipment.') },
            )
            flash(t('marketplace.pages.create.success', 'Shipment created successfully.'), 'success')
            const id = created.result?.id
            router.push(id ? `/backend/marketplace/${id}` : '/backend/marketplace')
          }}
        />
      </PageBody>
    </Page>
  )
}
